import React from 'react';
import { Stack, Box, Typography, Button, IconButton, useMediaQuery, useTheme, alpha, FormControl, Select, MenuItem } from '@mui/material';
import { ChevronLeft, ChevronRight, Close, Schedule } from '@mui/icons-material';
import { FaChevronLeft } from 'react-icons/fa';

interface CalendarHeaderProps {
    currentMonth: Date;
    onPrevMonth: () => void;
    onNextMonth: () => void;
    onClose: () => void;
    onSelectMonthYear: (newDate: Date) => void;
    onAddService?: () => void;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
    currentMonth,
    onPrevMonth,
    onNextMonth,
    onClose,
    onSelectMonthYear,
    onAddService,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const years = Array.from({ length: 100 }, (_, i) => 2000 + i);

    return (
        <Stack spacing={{ xs: 2, sm: 4 }} mt={{ xs: 2, sm: 0 }} mb={{ xs: 2, sm: 4 }}>
            {/* Header Top Section */}
            <Box>
                <Box display="flex" alignItems="center" mb={1}>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: '#D45E8C',
                            backgroundColor: alpha('#F475A4', 0.1),
                            borderRadius: 2,
                            '&:hover': {
                                backgroundColor: alpha('#F475A4', 0.2),
                            }
                        }}
                    >
                        <FaChevronLeft />
                    </IconButton>
                </Box>

                {onAddService && (
                    <Box display="flex" justifyContent="center" mb={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onAddService}
                            sx={{
                                borderRadius: 3,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: { xs: '0.75rem', sm: '1rem' },
                                py: { xs: 1, sm: 2 },
                                boxShadow: '0 4px 12px rgba(244, 117, 164, 0.2)',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%': { transform: 'scale(1)', boxShadow: '0 4px 12px rgba(244, 117, 164, 0.2)' },
                                    '50%': { transform: 'scale(1.05)', boxShadow: '0 8px 20px rgba(244, 117, 164, 0.4)' },
                                    '100%': { transform: 'scale(1)', boxShadow: '0 4px 12px rgba(244, 117, 164, 0.2)' },
                                }
                            }}
                        >
                            Select Your Service
                        </Button>
                    </Box>
                )}

                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                        sx={{
                            width: { xs: 40, sm: 50 },
                            height: { xs: 40, sm: 50 },
                            borderRadius: 2.5,
                            background: 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 6px 20px rgba(244, 117, 164, 0.4)'
                        }}
                    >
                        <Schedule sx={{ color: '#fff', fontSize: { xs: 24, sm: 30 } }} />
                    </Box>
                    <Typography
                        variant="h4"
                        fontWeight={800}
                        sx={{
                            fontSize: { xs: '1.3rem', sm: '2rem' },
                            background: 'linear-gradient(135deg, #F475A4 0%, #D45E8C 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}
                    >
                        Calendar View
                    </Typography>
                </Stack>
            </Box>

            {/* Navigation Row */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={0.5}
            >
                <Button
                    startIcon={!isMobile && <ChevronLeft />}
                    onClick={onPrevMonth}
                    variant="contained"
                    size="small"
                    sx={{
                        minWidth: { xs: 30, sm: 100 },
                        px: { xs: 0, sm: 1 },
                        borderRadius: 2.5,
                        background: 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)',
                        boxShadow: '0 4px 16px rgba(244, 117, 164, 0.3)',
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(244, 117, 164, 0.4)',
                            transform: 'translateY(-2px)'
                        }
                    }}
                >
                    {isMobile ? <ChevronLeft /> : 'Previous'}
                </Button>

                {/* Selectors - No Scroll, Flex wrapping if needed but optimized for space */}
                <Stack direction="row" spacing={1} flex={1} justifyContent="center">
                    <FormControl size="small" sx={{ minWidth: { xs: 75, sm: 130 }, maxWidth: { xs: 110, sm: 'none' } }}>
                        <Select
                            value={currentMonth.getMonth()}
                            onChange={(e) => {
                                const newDate = new Date(currentMonth);
                                newDate.setMonth(Number(e.target.value));
                                onSelectMonthYear(newDate);
                            }}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 700,
                                background: alpha('#F475A4', 0.08),
                                color: '#D45E8C',
                                fontSize: { xs: '0.85rem', sm: '1rem' },
                                '& fieldset': { borderColor: '#F475A4' },
                                '& .MuiSelect-select': { py: { xs: 0.8, sm: 1 } }
                            }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                        >
                            {months.map((m, i) => (
                                <MenuItem key={i} value={i}>{isMobile ? m.slice(0, 3) : m}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: { xs: 75, sm: 110 }, maxWidth: { xs: 90, sm: 'none' } }}>
                        <Select
                            value={currentMonth.getFullYear()}
                            onChange={(e) => {
                                const newDate = new Date(currentMonth);
                                newDate.setFullYear(Number(e.target.value));
                                onSelectMonthYear(newDate);
                            }}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 700,
                                background: alpha('#F475A4', 0.08),
                                color: '#D45E8C',
                                fontSize: { xs: '0.85rem', sm: '1rem' },
                                '& fieldset': { borderColor: '#F475A4' },
                                '& .MuiSelect-select': { py: { xs: 0.8, sm: 1 } }
                            }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                        >
                            {years.map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Button
                    endIcon={!isMobile && <ChevronRight />}
                    onClick={onNextMonth}
                    variant="contained"
                    size="small"
                    sx={{
                        minWidth: { xs: 30, sm: 100 },
                        px: { xs: 0, sm: 3 },
                        borderRadius: 2.5,
                        background: 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)',
                        boxShadow: '0 4px 16px rgba(244, 117, 164, 0.3)',
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(244, 117, 164, 0.4)',
                            transform: 'translateY(-2px)'
                        }
                    }}
                >
                    {isMobile ? <ChevronRight /> : 'Next'}
                </Button>
            </Stack>
        </Stack>
    );
};

export default CalendarHeader;
